/**
 * @fileoverview Story service for bedtime stories feature
 * Manages story loading, progress tracking, and purchase state.
 * All storage operations are delegated to StorageService.
 */

import { StorageService } from './StorageService.js';
import { currentLang } from '../i18n/lang.js';
import type { SupportedLang } from '../i18n/lang.js';

export interface StoryPage {
    pageNumber: number;
    imageUrl: string;
    text: string;
    altText: string;
}

/** Lightweight story metadata returned by the catalog (no pages). */
export interface StorySummary {
    id: string;
    title: string;
    thumbnail: string;
    price: number;
    isFree: boolean;
    isPurchased?: boolean;
    category: string;
    age: number;
    pageCount: number;
}

/** Full story with page content, fetched on demand. */
export interface Story extends StorySummary {
    pages: StoryPage[];
}

export interface StoryProgress {
    storyId: string;
    currentPage: number;
    completed: boolean;
    lastRead: number;
}

/** Locale overlay file — only the translatable fields, no image URLs. */
interface StoryTranslation {
    title: string;
    pages: Array<{ text: string; altText: string }>;
}

/**
 * Story service for managing bedtime stories
 */
export class StoryService {

    /** Base URL for stories data.
     *  Phase A: static files served from /data/ (Vite publicDir).
     *  Phase B+: point to S3/CloudFront URL without code changes. */
    private static BASE_URL = '/data';

    /**
     * Try to fetch a locale overlay for a story (e.g. barnaby.de.json).
     * Falls back to the English overlay, then to null (base JSON).
     */
    private static async loadTranslation(storyId: string, lang: SupportedLang): Promise<StoryTranslation | null> {
        const candidates = lang === 'en' ? ['en'] : [lang, 'en'];
        for (const l of candidates) {
            try {
                const res = l === 'en'
                    ? await fetch(`${this.BASE_URL}/stories/${storyId}/${storyId}.json`)
                    : await fetch(`${this.BASE_URL}/stories/${storyId}/${storyId}.${l}.json`);
                if (res.ok) {
                    const data = await res.json() as StoryTranslation;
                    if (data?.title && Array.isArray(data?.pages)) return data;
                }
            } catch {
                // fetch failed, JSON parse failed, or missing fields — try next candidate
            }
        }
        return null;
    }

    /**
     * Merge a translation overlay into a full Story object in-place.
     */
    private static applyTranslation(story: Story, t: StoryTranslation): void {
        story.title = t.title;
        story.pages = story.pages.map((page, i) => ({
            ...page,
            text:    t.pages[i]?.text    ?? page.text,
            altText: t.pages[i]?.altText ?? page.altText,
        }));
    }

    /**
     * Load the lightweight story catalog (no page content).
     * Used for the story list/grid screen.
     */
    static async loadCatalog(): Promise<StorySummary[]> {
        try {
            const response = await fetch(`${this.BASE_URL}/stories-catalog.json`);
            if (!response.ok) {
                throw new Error(`Failed to fetch catalog: ${response.status}`);
            }
            const data: { stories: StorySummary[] } = await response.json();
            const stories = data.stories ?? [];

            // Mark purchased stories
            const purchased = this.getPurchasedStories();
            stories.forEach(story => {
                story.isPurchased = story.isFree || purchased.includes(story.id);
            });

            // Apply translated titles when available
            if (currentLang !== 'en') {
                const translations = await Promise.all(
                    stories.map(s => this.loadTranslation(s.id, currentLang))
                );
                stories.forEach((s, i) => {
                    if (translations[i]) s.title = translations[i]!.title;
                });
            }

            return stories;
        } catch (e) {
            console.error('Could not load story catalog:', e);
            return [];
        }
    }

    /**
     * Load a single full story by ID (including pages).
     * Fetched on demand when the user opens a story.
     */
    static async loadStory(storyId: string): Promise<Story | null> {
        try {
            const [response, translation] = await Promise.all([
                fetch(`${this.BASE_URL}/stories/${storyId}/${storyId}.json`),
                this.loadTranslation(storyId, currentLang),
            ]);
            if (!response.ok) {
                throw new Error(`Failed to fetch story ${storyId}: ${response.status}`);
            }
            const story: Story = await response.json();

            // Mark purchase state
            const purchased = this.getPurchasedStories();
            story.isPurchased = story.isFree || purchased.includes(story.id);

            // Apply locale overlay if available
            if (translation) this.applyTranslation(story, translation);

            return story;
        } catch (e) {
            console.error(`Could not load story "${storyId}":`, e);
            return null;
        }
    }
    
    /**
     * Load story progress
     */
    static getStoryProgress(storyId: string): StoryProgress | null {
        return StorageService.getInstance().loadStoryProgress(storyId);
    }
    
    /**
     * Save story progress
     */
    static saveProgress(storyId: string, page: number): void {
        StorageService.getInstance().saveStoryProgress(storyId, page);
    }
    
    /**
     * Mark story as completed
     */
    static markCompleted(storyId: string): void {
        StorageService.getInstance().markStoryCompleted(storyId);
    }
    
    /**
     * Check if a story has been purchased
     */
    static isPurchased(storyId: string): boolean {
        return StorageService.getInstance().isStoryPurchased(storyId);
    }
    
    /**
     * Get list of purchased story IDs
     */
    static getPurchasedStories(): string[] {
        return StorageService.getInstance().getPurchasedStories();
    }
    
    /**
     * Mark story as purchased (after payment verification)
     */
    static markPurchased(storyId: string): void {
        StorageService.getInstance().markStoryPurchased(storyId);
    }
    
    /**
     * Initiate purchase flow (mock for MVP)
     */
    static async initiatePurchase(storyId: string, price: number): Promise<boolean> {
        // Mock purchase for MVP - just confirm and unlock
        const confirmed = confirm(
            `Purchase story for $${price.toFixed(2)}?\n\n` +
            '(This is a test - no real payment will be charged)'
        );
        
        if (confirmed) {
            this.markPurchased(storyId);
            return true;
        }
        
        return false;
    }
    
    /**
     * Preload story images for smoother experience
     */
    static async preloadImages(story: Story): Promise<void> {
        const imagePromises = story.pages.map(page => {
            return new Promise<void>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve();
                img.onerror = () => reject();
                img.src = page.imageUrl;
            });
        });
        
        try {
            await Promise.all(imagePromises);
            console.log(`Preloaded ${story.pages.length} images for story: ${story.title}`);
        } catch (e) {
            console.warn('Failed to preload some images:', e);
        }
    }
    
    /**
     * Reset all story data (for testing)
     */
    static resetAllData(): void {
        StorageService.getInstance().clearStoryData();
    }
}
